/**
 * Master enum of every telemetry event name emitted across the weavix monorepo.
 *
 * ## Adding a new event
 *
 * 1. Add a member to this enum.
 * 2. Run `yarn generate` from `packages/telemetry`.
 *    - `generate-event-stubs.cjs` creates `src/events/<kebab-name>.ts` (stub).
 *    - `generate-index.cjs` updates `src/telemetry-event-payloads.ts` and `src/index.ts`.
 * 3. Open the new stub in `src/events/` and fill in the payload fields by
 *    inspecting the call site(s) where the event is emitted. Document the
 *    interface and every property: the event dictionary published for the
 *    platform is generated from these comments.
 * 4. Emit it — `sendTelemetry` from client code, `sendTelemetryEvent` from
 *    server-side code. Device, user, account and app attributes are added by
 *    the sender, so a payload carries only its own fields.
 */
export enum TelemetryEvent {
    // --- PTT ---
    /** PTT audio message was sent by the local device. */
    PttSent = 'PttSent',
    /** Playback jitter detected on a received PTT (buffer underrun / lag). */
    PttJitter = 'PttJitter',
    /** Packet loss / jitter detected on a sent PTT. */
    PttSentJitter = 'PttSentJitter',
    /** Incoming PTT was skipped without playing (muted, not in facility, etc.). */
    PttSkipped = 'PttSkipped',
    /** Active PTT channel update was not pushed to the OS because the session is suspended. */
    PttChannelUpdateSkipped = 'PttChannelUpdateSkipped',
    /** Outgoing PTT attempt was denied because the channel was already occupied. */
    PttBuzzed = 'PttBuzzed',
    /** PTT hardware button was physically pressed. */
    PttPressed = 'PttPressed',
    /** PTT audio message was received and played back. */
    PttReceive = 'PttReceive',
    /** PTT playback was interrupted by a higher-priority audio event. */
    PttInterrupted = 'PttInterrupted',
    /** Held PTT key recovered after the channel cleared (denial resolved). */
    PttDenialRecovered = 'PttDenialRecovered',
    /** A press the user made that never transmitted: the key was released while the channel was still occupied, or the press was cancelled before the microphone opened. See `reason`. */
    PttDenialReleased = 'PttDenialReleased',
    /** Lifecycle stage marker emitted at multiple points in the PTT send/receive flow. */
    PttTimeline = 'PttTimeline',
    /** Network quality metrics sampled during PTT (native Android). */
    PttNetworkTelemetry = 'PttNetworkTelemetry',
    /** Per-PTT packet summary emitted after playback ends on Walt (native Android). */
    PttPacketTelemetry = 'PttPacketTelemetry',
    /** Periodic packet-count summary per network type on Walt (native Android). */
    PttPacketCount = 'PttPacketCount',
    /** Per-PTT summary of a PTT a walt relayed between LoRa and DECT, in either direction (native Android). */
    PttLoraRelay = 'PttLoraRelay',
    /** Per-PTT outcome of a PTT transcript a walt received in-band over LoRa (native Android). */
    PttLoraTranscript = 'PttLoraTranscript',
    /** The account-level LoRa kill switch (FeatureId.DisableLora) as a walt applied it, on each change (walt). */
    LoraKillSwitchChanged = 'LoraKillSwitchChanged',

    // --- PTT micro-service (ptt-service pod ownership) ---
    /** A PTT was assigned to a ptt-service pod, which then owns its stream. Emitted once per PTT by the pod that acquired it. */
    PttStreamAssigned = 'PttStreamAssigned',
    /** A PTT's stream completed normally and its owning pod released it. */
    PttStreamCompleted = 'PttStreamCompleted',
    /** A ptt-service pod took over a PTT whose previous owner stopped renewing its lease. */
    PttStreamRecovered = 'PttStreamRecovered',
    /** A ptt-service pod lost its ownership lease mid-PTT and stood down. */
    PttStreamOwnershipLost = 'PttStreamOwnershipLost',
    /** A PTT was discarded by ptt-service without being processed. */
    PttStreamDropped = 'PttStreamDropped',
    /** A clip's scratch recording could not be read back, so its stream was destroyed. */
    RecordingStreamFailed = 'RecordingStreamFailed',
    /** A ptt-service pod finished draining its in-flight PTTs during shutdown. */
    PttServiceDrained = 'PttServiceDrained',
    /** ptt-service resolved a PTT's recipients and began forwarding its audio to them. */
    PttFanoutStarted = 'PttFanoutStarted',
    /** ptt-service could not forward a PTT that had listeners, so that audio was never heard. */
    PttFanoutFailed = 'PttFanoutFailed',
    /** ptt-service could not hand a PTT to the backend, so it produces no transcription or message. */
    PttHandoffFailed = 'PttHandoffFailed',
    /** A peer claimed a handoff a pod died holding and retried the pickup; the pickup may still fail. */
    PttHandoffRecovered = 'PttHandoffRecovered',
    /** A PTT's audio could not be converted to PCM, so it gets no transcript or translation. */
    PttPcmConversionFailed = 'PttPcmConversionFailed',

    // --- Speech / translation ---
    /** Speech was transcribed to text — a PTT channel transmission or walt dictation, per `surface`. */
    SpeechTranscriptionEvent = 'SpeechTranscriptionEvent',
    /** A transcription cascade circuit breaker opened, taking an engine's account or a pooled Azure region out of use for its cooldown. */
    CascadeCircuitOpened = 'CascadeCircuitOpened',
    /** walt's on-device recognizer finished a mesh PTT without a usable transcript. */
    MeshTranscriptionDegraded = 'MeshTranscriptionDegraded',
    /** A dictation session never opened the mic — the start cue's completion never reached the guard that arms it. */
    SttCaptureSuppressed = 'SttCaptureSuppressed',
    /** Source language was detected via Azure Cognitive Services. */
    TextIdentificationEvent = 'TextIdentificationEvent',
    /** Text was translated -- by Azure on the server or by MADLAD on a walt, per `engine`. */
    TextTranslationEvent = 'TextTranslationEvent',
    /** Server TTS could not look up a site's respellings, so it spoke the text as sent. */
    TextToSpeechPronunciationLookupFailed = 'TextToSpeechPronunciationLookupFailed',
    /** The on-device MADLAD model failed to translate a mesh transcript. */
    OnDeviceTranslationFailed = 'OnDeviceTranslationFailed',
    /** An exchange the walt edge Translator recorded offline was dropped before it reached the session history. */
    TranslationCaptureLost = 'TranslationCaptureLost',

    // --- Networking / MQTT ---
    /** A client connected via MQTT. */
    MqttConnect = 'MqttConnect',
    /** A client disconnected from MQTT. */
    MqttDisconnect = 'MqttDisconnect',
    /** A mobile or walt client moved between Aedes and EMQX, or could not ask which broker to use. */
    MqttBrokerSwitched = 'MqttBrokerSwitched',
    /** Console, Web Radio or Teams dropped publishes still queued in the browser that could not be sent as this user. */
    MqttOutboxCleared = 'MqttOutboxCleared',
    /** Diagnostic snapshot of a single MQTT message. */
    MqttPacket = 'MqttPacket',
    /** EMQX provisioning finished with authorization settings unapplied, so the broker may still cache auth decisions. */
    EmqxProvisioningFailed = 'EmqxProvisioningFailed',
    /** Device detected a poor network signal. */
    PoorNetwork = 'PoorNetwork',
    /** Snapshot of all visible Wi-Fi access points and the connected network. */
    ObservedNetworks = 'ObservedNetworks',
    /** Device roamed to a different Wi-Fi access point. */
    WifiAccessPointChange = 'WifiAccessPointChange',
    /** Device changed LTE cell tower. */
    LteTowerChange = 'LteTowerChange',
    /** A preferred SIM failed to register on the cellular network with no registered SIM to fall back to. */
    StubbornSim = 'StubbornSim',
    /** Wi-Fi was disconnected and later reconnected; records the gap duration. */
    WifiNetworkDisconnectedDuration = 'WifiNetworkDisconnectedDuration',
    /** Mobile data was disconnected and later reconnected; records the gap duration. */
    MobileNetworkDisconnectedDuration = 'MobileNetworkDisconnectedDuration',
    /** Both Wi-Fi and mobile data were simultaneously disconnected; records the gap duration. */
    AllNetworksDisconnectedDuration = 'AllNetworksDisconnectedDuration',
    /** Known facility Wi-Fi networks were detected nearby for the first time. */
    FacilityNetworksFoundEvent = 'FacilityNetworksFoundEvent',
    /** The set of detected facility Wi-Fi networks changed. */
    FacilityNetworksUpdateEvent = 'FacilityNetworksUpdateEvent',

    // --- Location ---
    /** Location fix computed via the Combain Wi-Fi/cell positioning API. */
    CombainLocationTelemetry = 'CombainLocationTelemetry',
    /** Location fix computed via beacon trilateration. */
    TrilaterationTelemetry = 'TrilaterationTelemetry',
    /** A location fix was reported to the platform. */
    LocationData = 'LocationData',
    /** Periodic heartbeat confirming the location pipeline is alive. */
    LocationHeartbeat = 'LocationHeartbeat',
    /** The mobile LocationManager started producing fixes. */
    LocationManagerStart = 'LocationManagerStart',
    /** The mobile LocationManager stopped producing fixes. */
    LocationManagerStop = 'LocationManagerStop',
    /** A caller was refused a location history read (Live Look, backtrack, geofence history, walt sessions). */
    LocationReadDenied = 'LocationReadDenied',
    /** A permission check passed only because the site-settings fallback stood in for a geofence, floor plan or item action. */
    SiteSettingsFallbackUsed = 'SiteSettingsFallbackUsed',
    /** The GPS hardware provider started. */
    GpsStart = 'GpsStart',
    /** The GPS hardware provider stopped. */
    GpsStop = 'GpsStop',
    /** Mobile motion monitoring cannot detect movement without steps, so a still device stops trusting an old location fix. */
    MotionMonitoringUntrusted = 'MotionMonitoringUntrusted',

    // --- Backend health / auth ---
    /** A deprecated high-load endpoint was called. */
    HeavyRequest = 'HeavyRequest',
    /** An abusive or deprecated endpoint was called. */
    EvilUserRequest = 'EvilUserRequest',
    /** A MongoDB query hit the configured result-count limit. */
    QueryLimitEvent = 'QueryLimitEvent',
    /** An Atlas Search index could not be listed or created, or its build failed, so search on that collection is unavailable. */
    SearchIndexProvisionFailed = 'SearchIndexProvisionFailed',
    /** A search-index-sync run finished or failed, with how many databases' search indexes were ready, building, created or failed. */
    SearchIndexSyncRun = 'SearchIndexSyncRun',
    /** The Node.js event loop was blocked for too long. */
    ServerBlocked = 'ServerBlocked',
    /** The Hermes JS thread was blocked for too long. */
    HermesBlocked = 'HermesBlocked',
    /** Node.js event-loop block with a profiler stack trace. */
    ServerBlockedAt = 'ServerBlockedAt',
    /** Hermes JS-thread block with a profiler stack trace. */
    HermesBlockedAt = 'HermesBlockedAt',
    /** Mobile/walt JS thread blocks. May not include very short blocks. */
    JavaBlockedAt = 'JavaBlockedAt',
    /** Web client JS thread block detected. */
    ClientJsBlocked = 'ClientJsBlocked',
    /** A request was authenticated with an API key. */
    AccountApiKeyUsed = 'AccountApiKeyUsed',

    // --- App lifecycle ---
    /** App moved to the background. */
    AppBackgrounded = 'AppBackgrounded',
    /** App returned to the foreground. */
    AppForegrounded = 'AppForegrounded',
    /** App restarted unexpectedly (crash or OOM kill). */
    AppUnexpectedRestart = 'AppUnexpectedRestart',
    /** App restarted intentionally (e.g. after an update). */
    AppExpectedRestart = 'AppExpectedRestart',
    /** App launched cold (first start). */
    AppLaunch = 'AppLaunch',
    /** Periodic app-activity snapshot. */
    ActivityMonitor = 'ActivityMonitor',
    /** Periodic resource-usage snapshot (CPU, memory, network, battery). */
    ResourcesUsed = 'ResourcesUsed',
    /** Ambient noise level sampled while the user is on-site (Walt only). */
    AmbientNoiseSample = 'AmbientNoiseSample',
    /** Screen was kept awake longer than the expected maximum duration. */
    ScreenAwakeTooLong = 'ScreenAwakeTooLong',
    /** Lifecycle stage marker emitted throughout the mobile startup sequence. */
    MobileStartupTimeline = 'MobileStartupTimeline',

    // --- Auth / login ---
    /** Mobile app startup duration (misnamed, includes non-login resumes). */
    LoginTime = 'LoginTime',
    /** User completed a fresh login on mobile. */
    MobileLogin = 'MobileLogin',
    /** User logged out on mobile. */
    MobileLogout = 'MobileLogout',
    /** Backend refused a PIN reset code redeemed with a login that is not its owner's. */
    PinResetCodeRefused = 'PinResetCodeRefused',
    /** Terminal outcome of a local-login upgrade's post-install handoff on Walt. */
    WaltLocalLoginUpgradeCompletion = 'WaltLocalLoginUpgradeCompletion',
    /** Native invalidated a provisional session; excludes JavaScript-initiated logout. */
    WaltLocalLoginInvalidated = 'WaltLocalLoginInvalidated',
    /** A walt scan could not sign in offline: no local credential, or the local login lease had expired. */
    WaltOfflineLoginUnavailable = 'WaltOfflineLoginUnavailable',

    // --- User / account provisioning ---
    /** A user accepted an account invite, including partial-state recovery outcomes. */
    AccountInviteAcceptance = 'AccountInviteAcceptance',
    /** A user was deleted, emitted per-account during cleanup and once overall. */
    UserDeletion = 'UserDeletion',
    /** A user's read-aloud voice was changed, by the user or an admin. */
    UserVoiceChanged = 'UserVoiceChanged',

    // --- Messaging / channels ---
    /** Client detected a gap in the channel message sequence. */
    MissingMessages = 'MissingMessages',
    /** Client failed to back-fill a batch of missing channel messages. */
    MissingMessagesFetchFailed = 'MissingMessagesFetchFailed',
    /** Channel read-sequence drifted and was auto-corrected. */
    ChannelSelfCorrection = 'ChannelSelfCorrection',
    /** User added a channel to their favourites. */
    ChannelFavorited = 'ChannelFavorited',
    /** User removed a channel from their favourites. */
    ChannelUnfavorited = 'ChannelUnfavorited',
    /** User hid a channel from their list; it returns with its next message. */
    ChannelHidden = 'ChannelHidden',
    /** User muted a channel's push-to-talk audio on all their devices (`allowPtt` off). */
    ChannelMuted = 'ChannelMuted',
    /** User turned a channel's push-to-talk audio back on (`allowPtt` on). */
    ChannelUnmuted = 'ChannelUnmuted',
    /** User silenced a channel's notifications on all their devices (`allowNotifications` off). */
    ChannelSilenced = 'ChannelSilenced',
    /** User turned a channel's notifications back on (`allowNotifications` on). */
    ChannelUnsilenced = 'ChannelUnsilenced',
    /** A mute or silence change could not be saved; the channel keeps its previous setting. */
    ChannelPreferenceSaveFailed = 'ChannelPreferenceSaveFailed',
    /** A user started a new channel and the server saved it, or returned the existing one with the same people. */
    ChannelCreated = 'ChannelCreated',
    /** A user's new channel could not be saved; the draft stays open so nothing they entered is lost. */
    ChannelCreateFailed = 'ChannelCreateFailed',
    /** A user added someone to a group channel from its details. */
    ChannelMemberAdded = 'ChannelMemberAdded',
    /** A user removed someone from a group channel from its details. */
    ChannelMemberRemoved = 'ChannelMemberRemoved',
    /** A user left a group channel; they stop getting its messages until someone adds them back. */
    ChannelLeft = 'ChannelLeft',
    /** A user renamed a group channel, or cleared its name back to the member names. */
    ChannelRenamed = 'ChannelRenamed',
    /** A change made from a channel's details (add, remove, rename or leave) could not be saved. */
    ChannelDetailsChangeFailed = 'ChannelDetailsChangeFailed',
    /** Desktop app: the server accepted a user's page to the other person on a 1:1 channel. */
    ChannelPaged = 'ChannelPaged',
    /** Desktop app: a page to the other person on a 1:1 channel could not be sent. */
    ChannelPageFailed = 'ChannelPageFailed',
    /** A client could not read the user's permissions for an account, so it hides what they gate (e.g. the directory). */
    PermissionsLoadFailed = 'PermissionsLoadFailed',
    /** A client's exact email or phone directory lookup failed, so no one outside the user's directory was shown. */
    DirectorySearchFailed = 'DirectorySearchFailed',
    /** A user ran a global search across messages, channels, people and crafts. Never carries the search text. */
    GlobalSearch = 'GlobalSearch',
    /** The message part of a global search failed or its index was not ready, so the user got an error instead of results. */
    ChannelMessageSearchFailed = 'ChannelMessageSearchFailed',
    /** A speech-to-text chunk arrived after a timed-out PTT message and resolved it. */
    ChannelMessageFailureResolved = 'ChannelMessageFailureResolved',
    /** A client-created PTT never reached the backend and its bubble was settled as failed. */
    ChannelMessageSendFailed = 'ChannelMessageSendFailed',
    /** A PTT's bubble settled with its audio intact but no transcript. */
    ChannelMessageTranscriptionMissing = 'ChannelMessageTranscriptionMissing',
    /** A message was created and dispatched to channel subscribers. */
    ChannelMessageNotify = 'ChannelMessageNotify',
    /** Media attached to a message never left the device and the message was marked failed. */
    ChannelAttachmentSendFailed = 'ChannelAttachmentSendFailed',
    /** A walt could not read an incoming channel message aloud, or the message had nothing to speak. */
    ChannelMessageReadAloudFailed = 'ChannelMessageReadAloudFailed',
    /** A walt finished reading a translated PTT aloud; see `ChannelMessageReadAloudPayload` for what is sampled. */
    ChannelMessageReadAloud = 'ChannelMessageReadAloud',
    /** A walt edge spoke one utterance with its on-device voice outside a translated PTT (an alert, announcement or typed message); see `TtsSpokenPayload` for what is sampled. */
    TtsSpoken = 'TtsSpoken',
    /** A client could not load a page of a channel's message history; the messages already shown stay. */
    ChannelHistoryLoadFailed = 'ChannelHistoryLoadFailed',
    /** A client could not play a channel message's audio: the original recording or its text-to-speech. */
    ChannelAudioPlaybackFailed = 'ChannelAudioPlaybackFailed',
    /** A client could not show a channel message's photo or video thumbnail; the row shows a placeholder instead. */
    ChannelMediaLoadFailed = 'ChannelMediaLoadFailed',
    /** A client could not mark a channel read, so its unread count stays until the next try. */
    ChannelMarkReadFailed = 'ChannelMarkReadFailed',
    /** A client could not load a page of a channel's media, files or links history; the items already shown stay. */
    ChannelMediaHistoryLoadFailed = 'ChannelMediaHistoryLoadFailed',
    /** A client could not show, open or download a channel's shared photo, video, file or link. */
    ChannelAttachmentOpenFailed = 'ChannelAttachmentOpenFailed',
    /** A client could not go to the message a shared photo, video, file or link was sent in. */
    ChannelMessageRevealFailed = 'ChannelMessageRevealFailed',
    /** User's read position advanced, marking messages as read. */
    MessageRead = 'MessageRead',

    // --- Support / feedback ---
    /** Zendesk sent a reply or status-change webhook for a support ticket. */
    ZendeskReplyWebhook = 'ZendeskReplyWebhook',
    /** A new Zendesk support ticket was created. */
    SupportTicketCreated = 'SupportTicketCreated',

    // --- Weather alerts ---
    /** A weather-alert workflow trigger was created, updated, or deleted (adoption / config metrics). */
    WeatherAlertTriggerConfigured = 'WeatherAlertTriggerConfigured',
    /** A polled weather alert was matched and assigned to an account's facility (fire / ingestion-latency metrics). */
    WeatherAlertFacilityMatched = 'WeatherAlertFacilityMatched',
    /** A weather alert poll completed successfully (feed-availability metric). */
    WeatherAlertPollCompleted = 'WeatherAlertPollCompleted',
    /** Matching a poll's alerts against an account's facilities failed. */
    WeatherAlertFacilityMatchFailed = 'WeatherAlertFacilityMatchFailed',
    /** A condition was detected during a weather alert poll that may result in data loss. */
    WeatherAlertPollDataLossRisk = 'WeatherAlertPollDataLossRisk',
    /** A weather alert provider fetch failed. */
    WeatherAlertPollFetchFailed = 'WeatherAlertPollFetchFailed',
    /** The NWS weather alert poll has not run successfully within its SLA. */
    WeatherAlertPollSlaMissed = 'WeatherAlertPollSlaMissed',
    /** A NWS zone geometry changed since it was last cached. */
    WeatherAlertZoneGeometryChanged = 'WeatherAlertZoneGeometryChanged',
    /** Fetching geometry for a NWS zone URL failed. */
    WeatherAlertZoneGeometryFetchFailed = 'WeatherAlertZoneGeometryFetchFailed',
    /** A NWS zone URL has no geometry available. */
    WeatherAlertZoneGeometryMissing = 'WeatherAlertZoneGeometryMissing',

    // --- UX / banners / music ---
    /** An in-app banner was shown to the user. */
    BannerDisplayed = 'BannerDisplayed',
    /** User tapped an in-app banner. */
    BannerClicked = 'BannerClicked',
    /** User dismissed an in-app banner by swiping. */
    BannerSwipedAway = 'BannerSwipedAway',
    /** User selected a music station (Walt only). */
    MusicStationSelected = 'MusicStationSelected',
    /** Music playback was interrupted (PTT, call, etc.). */
    MusicInterrupted = 'MusicInterrupted',
    /** User picked a theme (Light, Dark or Match device) in the Weavix Desktop App. */
    DesktopThemeChanged = 'DesktopThemeChanged',
    /** User saved their profile in the Weavix Desktop App. */
    DesktopProfileSaved = 'DesktopProfileSaved',
    /** User uploaded a new profile photo in the Weavix Desktop App. */
    DesktopProfilePhotoUploaded = 'DesktopProfilePhotoUploaded',
    /** User asked to change, or confirmed a change of, their sign-in phone or email in the Weavix Desktop App. */
    DesktopLoginChange = 'DesktopLoginChange',
    /** An area of the Weavix Desktop App window crashed and shows a fallback message; the rest of the window keeps working. */
    DesktopScreenCrashed = 'DesktopScreenCrashed',
    /** The Weavix Desktop App's live connection came back after it showed the offline screen. */
    DesktopReconnected = 'DesktopReconnected',
    /** A live push-to-talk send in the Weavix Desktop App was cut short by the app, not by the user releasing. */
    DesktopPttSendFailed = 'DesktopPttSendFailed',
    /** The microphone or speaker chosen in the Weavix Desktop App was unplugged, so the system default is used. */
    DesktopAudioDeviceFallback = 'DesktopAudioDeviceFallback',
    /** The Weavix Desktop App's push-to-talk receive subscription was refused by the broker, or subscribed again after a drop. */
    DesktopPttReceiveSubscription = 'DesktopPttReceiveSubscription',
    /** The Weavix Desktop App got push-to-talk audio it could not read or decode, so it played nothing for it. */
    DesktopPttReceiveFailed = 'DesktopPttReceiveFailed',
    /** A typed message, with or without files, could not be sent from the Weavix Desktop App and shows as Not sent. */
    DesktopMessageSendFailed = 'DesktopMessageSendFailed',
    /** An edit to the user's own message in the Weavix Desktop App was not saved, so the message went back to its old text. */
    DesktopMessageEditFailed = 'DesktopMessageEditFailed',
    /** An incoming page reached the Weavix Desktop App: rung, kept quiet, too late to ring, or on a group channel. */
    DesktopPageReceived = 'DesktopPageReceived',
    /** The Weavix Desktop App sent, or failed to send, an answered, declined or missed response to an incoming page. */
    DesktopPageResponse = 'DesktopPageResponse',
    /** The user pressed Space on an incoming page card in the Weavix Desktop App, so push-to-talk did not start; once per card. */
    DesktopPagePttBlocked = 'DesktopPagePttBlocked',
    /** A click on an incoming page card in the Weavix Desktop App came in its first 500 ms, so it was ignored and nothing was sent. */
    DesktopPageClickIgnored = 'DesktopPageClickIgnored',
    /** The user saved an edit to their own message in the Weavix Desktop App and the server accepted it. */
    DesktopMessageEdited = 'DesktopMessageEdited',
    /** A file attached in the Weavix Desktop App was refused or failed to upload, so it was dropped from the message. */
    DesktopAttachmentUploadFailed = 'DesktopAttachmentUploadFailed',
    /** The Weavix Desktop App's clock is more than 5 s off the server's, which it uses to tell live PTT from a backlog. */
    DesktopClockSkew = 'DesktopClockSkew',
    /** The Weavix Desktop App's emoji picker could not load its emoji, or the names in the app language, so it shows an error or English names. */
    DesktopEmojiPickerLoadFailed = 'DesktopEmojiPickerLoadFailed',
    /** User sent a Weavix Desktop App message with an emoji or GIFs picked from the composer's pickers; once per kind per message. */
    DesktopComposerRichContentUsed = 'DesktopComposerRichContentUsed',
    /** The Weavix Desktop App's GIF picker could not load trending or search results, or download the picked GIF, so it shows an error. */
    DesktopGifRequestFailed = 'DesktopGifRequestFailed',
    /** User saved Settings › Notifications (notifications, sound, previews, start at login) in the Weavix Desktop App. */
    DesktopNotificationSettingsSaved = 'DesktopNotificationSettingsSaved',
    /** User sent a test notification from Settings in the Weavix Desktop App, and whether the OS showed it. */
    DesktopTestNotificationSent = 'DesktopTestNotificationSent',
    /** User clicked a Weavix Desktop App message notification, opening its channel. */
    DesktopNotificationOpened = 'DesktopNotificationOpened',
    /** A Weavix Desktop App notification banner or app-icon badge was refused by the OS, or a message synced too late to notify; once per kind per run. */
    DesktopNotificationFailed = 'DesktopNotificationFailed',
    /** User forwarded a message to a channel from the Weavix Desktop App and the server accepted it; once per channel sent to. */
    DesktopMessageForwarded = 'DesktopMessageForwarded',
    /** A forward to a channel from the Weavix Desktop App failed, so its row shows Not sent; once per failed attempt. */
    DesktopMessageForwardFailed = 'DesktopMessageForwardFailed',

    // --- Integrations ---
    /** Microsoft Teams change-notification or validation event received. */
    TeamsSubscription = 'TeamsSubscription',

    // --- Sync ---
    /** Client connected and began the initial sync. */
    SyncStartup = 'SyncStartup',
    /** Summary of sync events deduplicated from simulcast. */
    SyncDeduplicate = 'SyncDeduplicate',
    /** Summary of missed sync events, either in sequence or from a network. */
    SyncEventMissing = 'SyncEventMissing',
    /** Per-minute walt assignment reads, split by whether the caller was recognised as that walt and so got its account's enabled actions. */
    WaltAssignmentRead = 'WaltAssignmentRead',
    /** Per-minute walt disconnects skipped because the disconnecting connection no longer owns the network slot. */
    WaltStaleDisconnectSkipped = 'WaltStaleDisconnectSkipped',
    /** The desired state of native sync changed (activated or deactivated, tracking who triggered it). */
    NativeSyncDesiredChange = 'NativeSyncDesiredChange',
    /** A native sync partition (Turbo Module) became active after completing its initial data fetch. */
    SyncPartitionActive = 'SyncPartitionActive',
    /** A native sync partition caught up with the server after MQTT moved to another broker. */
    SyncCatchUp = 'SyncCatchUp',
    /** A caller was refused a sync topic belonging to another account or user. */
    SyncTopicAccessDenied = 'SyncTopicAccessDenied',

    // --- Web page views ---
    /** User navigated to a console (admin portal) page. */
    ConsolePageViewed = 'ConsolePageViewed',
    /** User navigated to a web-radio page. */
    WebRadioPageViewed = 'WebRadioPageViewed',

    // --- Site survey ---
    /** Legacy facility survey record submitted. */
    SiteSurvey = 'SiteSurvey',
    /** A site-survey reading was created. */
    SiteSurveyReadingCreated = 'SiteSurveyReadingCreated',
    /** A site-survey observation was submitted. */
    SiteSurveyObservationSubmitted = 'SiteSurveyObservationSubmitted',
    /** A Walt BT device connected to the survey app. */
    SiteSurveyWaltConnected = 'SiteSurveyWaltConnected',
    /** A Walt BT device disconnected unexpectedly during a survey. */
    SiteSurveyWaltLost = 'SiteSurveyWaltLost',
    /** A Walt was abandoned (survey session ended without completion). */
    SiteSurveyWaltAbandoned = 'SiteSurveyWaltsAbandoned',
    /** A Walt took a survey observation (network scan). */
    SiteSurveyWaltObservationTaken = 'SiteSurveyWaltObservationTaken',

    // --- Battery / audio / docks ---
    /** End-of-battery-session summary published (Walt only). */
    BatterySessionPublish = 'BatterySessionPublish',
    /** A new battery session started (Walt only). */
    BatterySessionStart = 'BatterySessionStart',
    /** A queued audio item began playback. */
    QueuedAudioDidPlay = 'QueuedAudioDidPlay',
    /** Walt was placed in a dock that reported bad charging status. */
    BadDock = 'BadDock',
    /** Walt docked on wrangler power but did not read the wrangler's NFC tag, a sign of a dead NFC reader. */
    WranglerNfcMissed = 'WranglerNfcMissed',
    /** Sent when a Walt starts showing the badge reader error, then once a day for as long as it stays in that state. */
    WaltNfcReaderError = 'WaltNfcReaderError',
    /** End-of-audio-session summary published (Walt only). */
    AudioSessionPublish = 'AudioSessionPublish',
    /** The client audio manager ignored a stop request, e.g. a repeat for an event already stopping. */
    AudioEventStopRejected = 'AudioEventStopRejected',
    /** A phone call started or ended on an Android phone; app audio is superseded while it lasts. */
    PhoneCallStateChanged = 'PhoneCallStateChanged',
    /** Non-PTT app audio (alert, page, TTS) was denied, or any audio interrupted, because of a phone call. */
    AudioSupersededByPhoneCall = 'AudioSupersededByPhoneCall',

    // --- Walt ---
    /** Walt is connected to a weaker AP while a stronger known AP is visible. */
    WaltBetterWifiDetected = 'WaltBetterWifiDetected',
    /** Walt minted its device token with its backup credential after the current one was missing or rejected. */
    WaltCredentialRecovered = 'WaltCredentialRecovered',
    /** Walt credential registration failed. */
    WaltCredentialRegistrationFailed = 'WaltCredentialRegistrationFailed',
    /** Walt credential update failed. */
    WaltCredentialUpdateFailed = 'WaltCredentialUpdateFailed',
    /** Walt credential validation failed (backend or device side). */
    WaltCredentialValidationFailed = 'WaltCredentialValidationFailed',
    /** Walt found its server `loggedIn` missing while a user was logged in and queued a re-stamp; sent once per detection, before the server confirms it. */
    WaltLoggedInRestored = 'WaltLoggedInRestored',
    /** Walt is not on Wi-Fi but a known network is visible and in range. */
    WaltMissingWifiDetected = 'WaltMissingWifiDetected',
    /** PTT recording produced zero audio bytes. */
    WaltNothingRecorded = 'WaltNothingRecorded',
    /** A Console Refresh timed out without the walt reporting in (backend side). */
    WaltRefreshFailed = 'WaltRefreshFailed',
    /** A Console admin sent a restart, power-off or install-update command to a walt. */
    WaltCommandSent = 'WaltCommandSent',
    /** Reports whether the final "device status" packet made it out before Walt powered off. */
    WaltShutdownStatusReported = 'WaltShutdownStatusReported',

    // --- Firmware ---
    /** Walt firmware update failed. */
    FirmwareUpdateError = 'FirmwareUpdateError',
    /** Walt firmware update completed successfully. */
    FirmwareUpdateSuccess = 'FirmwareUpdateSuccess',

    // --- AI ---
    /** AI voice assistant produced a response. */
    AIVoiceAssistantResponse = 'AIVoiceAssistantResponse',
    /** AI voice assistant encountered an error generating a response. */
    AIVoiceAssistantError = 'AIVoiceAssistantResponseError',

    // --- Offline mode ---
    /** Summary of a completed offline-messaging session. */
    OfflineModeSession = 'OfflineModeSession',
    /** Offline mode was requested for a facility. */
    OfflineModeRequested = 'OfflineModeRequested',
    /** Offline mode was cleared for a facility. */
    OfflineModeCleared = 'OfflineModeCleared',

    // --- Peripherals ---
    /** A USB-C peripheral disconnected. */
    UsbcDeviceDisconnected = 'UsbcDeviceDisconnected',
    /** A USB-C peripheral reconnected after a disconnect. */
    UsbcDeviceReconnected = 'UsbcDeviceReconnected',
    /** Short I-key press detected on Valor speaker-mic. */
    ShortValorIKeyUp = 'ShortValorIKeyUp',
    /** An NFC tag was scanned but not handled by the app. */
    NfcUnhandledRead = 'NfcUnhandledRead',
    /** An NFC write was attempted but not handled by the app. */
    NfcUnhandledWrite = 'NfcUnhandledWrite',
    /** A walt-1s ran its NFC antenna self-test after missing its wrangler tag on several docks in a row. */
    NfcSelfTest = 'NfcSelfTest',
    /** An unrecognised HID (USB input) signal was received. */
    UnexpectedHidSignal = 'UnexpectedHidSignal',
    /** Hatch speaker-mic plugged in or unplugged. */
    SpeakerMicConnected = 'SpeakerMicConnected',
    /** A key was pressed on the Hatch speaker-mic. */
    SpeakerMicKeyEvent = 'SpeakerMicKeyEvent',
    /** The Hatch speaker-mic flashlight state changed. */
    SpeakerMicFlashlightEvent = 'SpeakerMicFlashlightEvent',
    /** The Hatch speaker-mic headphone port state changed. */
    SpeakerMicHeadphoneEvent = 'SpeakerMicHeadphoneEvent',
    /** The Hatch speaker-mic reported an internal update. */
    SpeakerMicUpdate = 'SpeakerMicUpdate',
    /** Device volume changed. */
    VolumeChange = 'VolumeChange',
    /** A media (non-PTT) audio item started playing. */
    MediaPlayback = 'MediaPlayback',
    /** Outcome for a photo or video held by a walt's offline media queue. */
    OfflineMediaQueue = 'OfflineMediaQueue',

    // --- Mesh ---
    /** Lifecycle stage marker for mesh networking events. */
    MeshTimeline = 'MeshTimeline',
    /** Result of a mesh network connectivity test. */
    MeshConnectivityTest = 'MeshConnectivityTest',
    /** Periodic count of connected mesh peers (LoRa and DECT). */
    MeshPeersCount = 'MeshPeersCount',
    /** Server->mesh backfill run: how much a facility's returning gateways were caught up on. */
    MeshBackfill = 'MeshBackfill',
    /** A returning gateway resolved no facilities, so no backfill was sent at all. */
    MeshBackfillSkipped = 'MeshBackfillSkipped',
    /** Store-and-forward outbox finished replaying buffered messages after reconnecting to the mesh. */
    MeshOutboxFlushed = 'MeshOutboxFlushed',
    /** Periodic (5 min) snapshot of mesh radio window/slot occupancy, TX health and LoRa send health, aggregated natively. */
    MeshRadioStats = 'MeshRadioStats',
    /** Mesh radios (DECT + LoRa) were powered on or off, e.g. dock/undock power management. */
    MeshRadioPowerChanged = 'MeshRadioPowerChanged',
    /** Periodic (5 min) on-walt counters of mesh-to-cloud forwarding outcomes: connected peers stamped or missing, elected-but-offline drops, forwards performed. */
    MeshForwardStats = 'MeshForwardStats',
    /** One per SOS, facility alert or ack a walt sends or relays over LoRa. */
    MeshLoraSafety = 'MeshLoraSafety',
    /** Backend could not forward a PTT into the mesh: no connected forwarder was found, or some mesh recipients were unreachable through every chosen forwarder. */
    MeshForwardFailure = 'MeshForwardFailure',
    /** The walt user's weMesh on/off choice changed, from the settings switch or the reset at logout. */
    MeshUserToggleChanged = 'MeshUserToggleChanged',
    /** The walt applied a new Control Center `walt-mesh` value for its site. */
    MeshSiteFlagChanged = 'MeshSiteFlagChanged',
    /** Outcome of a walt clearing its own repeater placement after deliberately leaving Repeater Mode. */
    MeshRepeaterPlacementClear = 'MeshRepeaterPlacementClear',

    // --- Safety ---
    /** Beacon visibility report for a raised on-site SOS: counts, freshness and nearest distance — never a beacon identity. */
    SosBeaconScan = 'SosBeaconScan',
    /** User triggered or cleared a panic / SOS alert. */
    SosEvent = 'SosEvent',
    /** The mesh copy of an SOS failed entirely or skipped facilities with no mesh id. */
    SosMeshSendDegraded = 'SosMeshSendDegraded',
    /** A beacon-carrying SOS was deduped against a same-second PanicReceived without one, discarding the proximity beacon. */
    SosProximityBeaconDiscarded = 'SosProximityBeaconDiscarded',
    /** Emitted for every SOS/panic request the backend processes, per delivered request copy before dedupe — mesh simulcast means several per SOS. */
    SosReceived = 'SosReceived',
    /** A typed SOS was deduped against an untyped same-second PanicReceived, discarding its trigger type. */
    SosTriggerTypeDiscarded = 'SosTriggerTypeDiscarded',
    /** Undefined user detected at a point where a user should be set; aids auth-state debugging. */
    UndefinedUser = 'UndefinedUser',

    // --- Walt hardware metrics ---
    /** Periodic battery current-draw summary on Walt (sampled at 2 Hz). */
    BatteryCurrent = 'BatteryCurrent',
    /** Periodic system-wide CPU utilisation snapshot on Walt. */
    CpuUse = 'CpuUse',
    /** Periodic memory snapshot on Walt. */
    MemoryUse = 'MemoryUse',
    /** Periodic snapshot of Walt's data partition and Couchbase volume space. */
    StorageUse = 'StorageUse',
    /** Exit-monitoring module reported a device exit event. */
    ExitMonitoring = 'ExitMonitoring',

    // --- Change stream ---
    /** A MongoDB change-stream document was dropped before processing. */
    ChangeStreamDiscarded = 'ChangeStreamDiscarded',
    /** A MongoDB change-stream document was received and queued. */
    ChangeStreamEvent = 'ChangeStreamEvent',
    /** A change-stream document was exported to a downstream sink. */
    ChangeStreamExport = 'ChangeStreamExport',
    /** The change-stream consumer queue depth exceeded a threshold. */
    ChangeStreamQueuePressure = 'ChangeStreamQueuePressure',
    /** A change-stream document was transformed. */
    ChangeStreamTransform = 'ChangeStreamTransform',
    /** The change-stream consumer applied back-pressure to the source. */
    ChangeStreamBackPressure = 'ChangeStreamBackPressure',
    /** A large change-stream document was offloaded to Azure Blob Storage. */
    ChangeDocumentStoredInAzureBlob = 'ChangeDocumentStoredInAzureBlob',
    /** A change record was skipped because its account had been deleted; writing it would recreate the dropped database. */
    ChangeRecordSkipped = 'ChangeRecordSkipped',
    /** A change-stream consumer failed on an event whose account has since been deleted, so the event was dropped instead of retried. */
    ChangeEventSkipped = 'ChangeEventSkipped',

    // --- Telemetry stream / consumer infrastructure ---
    /** Per-event-type throughput counter emitted by change-stream consumers. */
    EventMetrics = 'EventMetrics',
    /** Telemetry-stream consumer queue depth exceeded a threshold. */
    TelemetryQueueBackPressure = 'TelemetryQueueBackPressure',
    /** A consumer operation exhausted all retry attempts. */
    RetryLimitExceeded = 'RetryLimitExceeded',
    /** A RabbitMQ consumer lost its channel and began retrying (`stillRetrying` while it fails), then `recovered` or `abandoned`. */
    RabbitMqConsumerReestablish = 'RabbitMqConsumerReestablish',
    /** An ingestion parquet writer stored NULL because a timestamp value did not parse. */
    ParquetTimestampUnparseable = 'ParquetTimestampUnparseable',
    /** An ingestion parquet writer stored NULL because a value did not match its column's declared DOUBLE or BOOLEAN type. */
    ParquetValueMismatched = 'ParquetValueMismatched',
    /** One account-month of badgeUpdates was archived to blob, appended to, mismatched, purged for retention, or failed. */
    BadgeUpdatesArchiveMonth = 'BadgeUpdatesArchiveMonth',
    /** A badgeUpdates archive run finished, failed, or found another run holding the archive. */
    BadgeUpdatesArchiveRun = 'BadgeUpdatesArchiveRun',
    /** The badgeUpdates archive dropped an archived month from Mongo, recorded an earlier drop, put one back, held, stranded or failed on one. */
    BadgeUpdatesArchiveMonthDropped = 'BadgeUpdatesArchiveMonthDropped',
    /** A badge history request (DVR playback or person location history) was served from Mongo, the parquet archive, or both. */
    BadgeHistoryQuery = 'BadgeHistoryQuery',
    /** A SQL report or data source named a badgeUpdates month that is no longer in Mongo; reports read Mongo only, so it saw no rows. */
    BadgeUpdatesArchivedMonthQueried = 'BadgeUpdatesArchivedMonthQueried',

    // --- Service lifecycle ---
    /** A backend service started up. */
    StartUp = 'Startup',
    /** A backend service shut down. */
    Shutdown = 'Shutdown',

    // --- Data retention functions ---
    /** Azure data-retention function deleted alert records. */
    AlertDeletion = 'AlertDeletion',
    /** Azure data-retention function deleted channel messages. */
    ChannelDeletion = 'ChannelDeletion',
    /** Azure data-retention function retrieved channels for processing. */
    ChannelRetrieval = 'ChannelRetrieval',
    /** Azure data-retention function deleted event records. */
    EventDeletion = 'EventDeletion',
    /** Azure data-retention function retrieved retention policies. */
    PolicyRetrieval = 'PolicyRetrieval',

    // --- Geofence / facility presence ---
    /** A person entered a geofence zone. */
    GeofenceEnter = 'GeofenceEnter',
    /** A person exited a geofence zone. */
    GeofenceExit = 'GeofenceExit',
    /** A person entered a facility (site). */
    FacilityEnter = 'FacilityEnter',
    /** A person exited a facility (site). */
    FacilityExit = 'FacilityExit',
    /** The phone left tracking circles the OS would refuse out of its geofence registration, instead of crashing on them. */
    TrackingCirclesRejected = 'TrackingCirclesRejected',

    // --- Facility alerts ---
    /** Delivering a facility alert to one recipient failed, without affecting delivery to any other recipient. */
    FacilityAlertPersonDeliveryFailed = 'FacilityAlertPersonDeliveryFailed',
    /** Creating a facility alert failed - e.g. a derived-id collision on `facilityAlerts.insert`. No recipient was notified. */
    FacilityAlertCreateFailed = 'FacilityAlertCreateFailed',
    /** Forwarding a facility alert to one language group's mesh recipients failed. Direct delivery is unaffected. */
    FacilityAlertMeshForwardFailed = 'FacilityAlertMeshForwardFailed',
    /** How a facility alert's mesh recipients are reached: DECT forwarders and LoRa gateways for isolated recipients. */
    FacilityAlertMeshForwarded = 'FacilityAlertMeshForwarded',
    /** A walt dropped a facility alert copy it already sounded from the other channel (mesh or network). */
    FacilityAlertDuplicateSuppressed = 'FacilityAlertDuplicateSuppressed',
    /** A walt freed a facility alert's id after it failed to show or closed unanswered, so its other copy may sound. */
    FacilityAlertCopyReleased = 'FacilityAlertCopyReleased',
    /** A walt failed to re-send the HTTP ack for a facility alert the worker had already answered. */
    FacilityAlertAckResendFailed = 'FacilityAlertAckResendFailed',

    // --- Site-scoped packages ---
    /** A package definition in `master.packages` was created or updated through the admin API. */
    ProductPackageDefinitionChanged = 'ProductPackageDefinitionChanged',
    /** A package definition write was refused because the resulting set of definitions would be invalid. */
    ProductPackageDefinitionRejected = 'ProductPackageDefinitionRejected',
    /** A site's packages or an account's default packages changed through the admin API. */
    ProductPackageAssignmentChanged = 'ProductPackageAssignmentChanged',
    /** A site or account-default package assignment was refused. */
    ProductPackageAssignmentRejected = 'ProductPackageAssignmentRejected',
    /** A permission check could not use a site's product packages and fell back to the account's `enabledActions`. */
    ProductPackageEntitlementFallback = 'ProductPackageEntitlementFallback',
    /**
     * A site's product packages denied an action the account's `enabledActions` grants, so the site has less than today.
     * Throttled to once per 10 minutes per pod per account, site and action, so count(*) is not request volume.
     */
    ProductPackageEntitlementDenied = 'ProductPackageEntitlementDenied',
    /**
     * A site's product packages granted an action the account's `enabledActions` withholds, so the site has more than today.
     * Throttled to once per 10 minutes per pod per account, site and action, so count(*) is not request volume.
     */
    ProductPackageEntitlementGranted = 'ProductPackageEntitlementGranted',
    /** A phone's update check required an update, found the store behind the required version, or failed. */
    MobileUpdateCheck = 'MobileUpdateCheck',
}
